import React from 'react'
import { Navigate } from 'react-router'
import { EVENT_TYPE_CONSULTATION, EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'
import {
  SUPPORT_BOOKINGS_VIEW_ROLES,
  SUPPORT_CASE_DISPATCH_ROLES,
  SUPPORT_CUSTOM_COMMUNICATIONS_VIEW_ROLES,
  SUPPORT_PAYMENT_LINKS_VIEW_ROLES,
  SUPPORT_VC_AUDIT_VIEW_ROLES,
} from '../access/accessMatrix'

// Direct import so /support-home renders without lazy chunk (avoids blank when chunk fails or RR v6 doesn't render lazy)
import SupportHomePage from '../views/home/SupportHomePage'
const TherapistsListing = React.lazy(() => import('../views/therapists/Therapists'))
const TherapistApplicantsPage = React.lazy(() =>
  import('../views/therapists/TherapistApplicantsPage'),
)
const ScreenersPage = React.lazy(() => import('../views/therapists/ScreenersPage'))
const TherapistsClinics = React.lazy(() => import('../views/therapists/clinics/TherapistClinics'))
const ScreenerClinics = React.lazy(() => import('../views/therapists/clinics/ScreenerClinics'))
const TherapistDayViewCal = React.lazy(() =>
  import('../views/therapists/clinics/bookingCalendar/TherapistBookings'),
)
const TherapistPayoutReports = React.lazy(() =>
  import('../views/payoutReports/TherapistPayoutReports'),
)
const TherapistEngagementModels = React.lazy(() =>
  import('../views/engagementModels/TherapistEngagementModels'),
)
const PayoutReceipts = React.lazy(() => import('../views/payoutReports/PayoutReceipts'))
const ClinicBrandInvoices = React.lazy(() => import('../views/payoutReports/ClinicBrandInvoices'))

const ClinicRosterPage = React.lazy(() => import('../views/roster/ClinicRosterPage'))
const OverallRosterPage = React.lazy(() => import('../views/rosterManagement/OverallRosterPage'))
const PractitionerRosterView = React.lazy(() => import('../views/rosterManagement/PractitionerRosterView'))
const TherapistRosterPage = React.lazy(() => import('../views/therapistRoster/TherapistRosterPage'))
const SingleTherapistRosterPage = React.lazy(() =>
  import('../views/therapistRoster/SingleTherapistRoster'),
)
const TherapistPayoutReceipts = React.lazy(() =>
  import('../views/therapists/payouts/TherapistPayoutReceipts'),
)
const DoctorsPayoutReceipts = React.lazy(() =>
  import('../views/doctors/payouts/DoctorsPayoutReceipts'),
)

// Static import so clinic payout-reports route renders without a lazy chunk (avoids "Can't find variable: Box" when chunk loads).
// Do not replace with React.lazy; ClinicPayoutReports and PaymentConfigMenu depend on MUI Box being in the same bundle.
import SingularClinicPayoutReports from '../views/payoutReports/SingularClinicPayoutReports'
const SingularClinicBrandPayoutReports = SingularClinicPayoutReports
const ClinicEngagementModels = React.lazy(() =>
  import('../views/engagementModels/ClinicEngagementModels'),
)
const SingularClinicBrandEngagementModels = React.lazy(() =>
  import('../views/engagementModels/SingularClinicEngagementModels'),
)

const ScheduledPatients = React.lazy(() =>
  import('../views/scheduledPatients/ScheduledPatients.jsx'),
)

const SingleTherapistEventsPage = React.lazy(() => import('../views/therapists/TherapistsEvents'))
const SupportTherapistAppointmentsPage = React.lazy(() =>
  import('../views/practitioners/therapists/SupportTherapistAppointmentsPage'),
)
const TherapistPayoutHubPage = React.lazy(() =>
  import('../views/practitioners/therapists/TherapistPayoutHubPage'),
)
const TherapistCommunicationsPage = React.lazy(() =>
  import('../views/practitioners/therapists/TherapistCommunicationsPage'),
)
const SupportPractitionerBundlePacksPage = React.lazy(() =>
  import('../views/bundlePacks/SupportPractitionerBundlePacksPage'),
)
const PractitionerOnboardingPage = React.lazy(() =>
  import('../views/practitioners/PractitionerOnboardingPage'),
)
const PractitionerInvitationsPage = React.lazy(() =>
  import('../views/practitioners/PractitionerInvitationsPage'),
)
// Phase 14i — listing for kyc-approved-but-disabled practitioners. Reachable from the new
// Practitioner Management nav tile.
const DisabledPractitionersPage = React.lazy(() =>
  import('../views/practitioners/DisabledPractitionersPage'),
)
// Platform-wide practitioner enable/disable access log.
const PractitionerAccessLogsPage = React.lazy(() =>
  import('../views/practitioners/PractitionerAccessLogsPage'),
)
const PractitionerPaymentConfigPage = React.lazy(() =>
  import('../views/practitioners/PractitionerPaymentConfigPage'),
)

const DoctorsListing = React.lazy(() => import('../views/doctors/DoctorsListing'))
const DoctorsClinics = React.lazy(() => import('../views/doctors/clinics/DoctorClinics'))
const DoctorDayViewCal = React.lazy(() =>
  import('../views/doctors/clinics/bookingCalendar/DoctorBookings'),
)
const DoctorsEventTypes = React.lazy(() => import('../views/doctors/DoctorsEventTypes'))
const SupportDoctorSettings = React.lazy(() =>
  import('../views/doctors/doctorSettings/SupportDoctorSettings'),
)
const SupportPrescriptionTemplates = React.lazy(() =>
  import('../views/doctors/prescriptionTemplates/SupportPrescriptionTemplates'),
)
const SupportPrescriptionTemplateViewPage = React.lazy(() =>
  import('../views/doctors/prescriptionTemplates/SupportPrescriptionTemplateViewPage'),
)

const ClinicsListing = React.lazy(() => import('../views/clinics/ClinicsListing'))
const SupportClinicPatientsPage = React.lazy(() => import('../views/clinics/patients/SupportClinicPatientsPage'))
const ClinicBrandPayoutInvoices = React.lazy(() =>
  import('../views/clinics/payouts/ClinicBrandPayoutInvoices'),
)
const SupportClinicServices = React.lazy(() =>
  import('../views/clinics/clinicServices/SupportClinicServices'),
)
const SupportClinicServicesDirectEntry = React.lazy(() =>
  import('../views/clinics/clinicServices/SupportClinicServicesDirectEntry'),
)
const SupportClinicBookings = React.lazy(() =>
  import('src/supportPortal/views/clinics/bookings/SupportClinicBookings'),
)
const SupportClinicLocationBundlePacksPage = React.lazy(() =>
  import('src/supportPortal/views/bundlePacks/SupportClinicLocationBundlePacksPage'),
)
const SupportClinicBrandBundlePacksPage = React.lazy(() =>
  import('src/supportPortal/views/bundlePacks/SupportClinicBrandBundlePacksPage'),
)
const PatientsNavPage = React.lazy(() => import('src/supportPortal/views/patients/PatientsNavPage'))
const PatientManagementNavPage = React.lazy(() => import('src/supportPortal/views/patients/PatientManagementNavPage'))
const PractitionerManagementNavPage = React.lazy(() => import('src/supportPortal/views/practitioners/PractitionerManagementNavPage'))
const ClinicsNavPage = React.lazy(() => import('src/supportPortal/views/clinics/ClinicsNavPage'))
const PatientsTherapyActions = React.lazy(() =>
  import('src/supportPortal/views/patients/therapyActions/PatientTherapyActions'),
)
const CareOrchestrationCasesPage = React.lazy(() =>
  import('src/supportPortal/views/patients/CareOrchestrationCasesPage'),
)
const PatientList = React.lazy(() => import('src/supportPortal/views/patients/PatientList'))
const PatientProspectsListPage = React.lazy(() =>
  import('src/supportPortal/views/patientProspects/PatientProspectsListPage'),
)
const BookedEvents = React.lazy(() => import('src/supportPortal/views/bookedEvents/BookedEvents'))
const CaseDispatchConsolePage = React.lazy(() =>
  import('src/supportPortal/views/caseDispatch/CaseDispatchConsolePage'),
)
const SupportPaymentLinksPage = React.lazy(() =>
  import('src/supportPortal/views/paymentLinks/SupportPaymentLinksPage'),
)
const CustomCommunicationsPage = React.lazy(() =>
  import('src/supportPortal/views/customCommunications/CustomCommunicationsPage'),
)
const BookingReportingPage = React.lazy(() => import('src/supportPortal/views/bookingReporting/BookingReportingPage'))
const ManagementReportingBookingsRedirect = () =>
  React.createElement(Navigate, {
    to: '/support-home/management-reporting/bookings',
    replace: true,
  })
const VcAuditSessionLiveViewer = React.lazy(() =>
  import('src/supportPortal/views/bookedEvents/VcAuditSessionLiveViewer'),
)
const VcAuditRecordingPlayer = React.lazy(() =>
  import('src/supportPortal/views/bookedEvents/VcAuditRecordingPlayer'),
)
const DocSideProducts = React.lazy(() => import('../views/docSide/DocSideProducts'))
const DocSideRoles = React.lazy(() => import('../views/docSide/DocSideRoles'))
const DocSideContexts = React.lazy(() => import('../views/docSide/DocSideContexts.jsx'))
const DocSideContent = React.lazy(() => import('../views/docSide/DocSideContent'))

const ServiceApprovals = React.lazy(() => import('../views/serviceApprovals/ServiceApprovalsListing'))
const SupportReportsViewer = React.lazy(() => import('../views/supportReportsViewer/SupportReportsViewer'))
const KycApprovalsListing = React.lazy(() => import('../views/kycApprovals/KycApprovalsListing'))
const KycApprovalForm = React.lazy(() => import('../views/kycApprovals/KycApprovalForm'))
const PrescriptionInterventionsListing = React.lazy(() =>
  import('../views/prescriptionInterventions/PrescriptionInterventionsListing'),
)
const AuditPage = React.lazy(() =>
  import('../views/auditRequests/AuditPage'),
)
const AuditRequestsListing = React.lazy(() =>
  import('../views/auditRequests/AuditRequestsListing'),
)

// Parent in App is path="/support-home/*". In RR v6, descendant <Routes> match against the
// REMAINING path (the splat), not the full pathname. So we use relative path for matching and
// fullPath for header/lookup (pathname is still full e.g. /support-home/...).
const P = '/support-home'
const DOCTOR_SUPPORT_ROLES = ['ROLE_CLINIC_SUCCESS_MANAGER', 'ROLE_CLINIC_SUCCESS_ADMIN']
const PRACTITIONER_NAV_ROLES = [
  'ROLE_THERAPY_SUP_L1',
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
]
const PRACTITIONER_ONBOARDING_ROLES = [
  'ROLE_THERAPIST_ADMIN',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_ADMIN',
]
const THERAPIST_PAYOUT_REPORT_ROLES = ['ROLE_THERAPIST_ADMIN']
const SERVICE_APPROVAL_ROLES = ['ROLE_THERAPY_SUP_L2', 'ROLE_THERAPY_SUP_L3', 'ROLE_CLINIC_SUCCESS_ADMIN']
const INTERVENTION_ROLES = [
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
]
const CARE_ORCHESTRATION_ROLES = [
  'ROLE_THERAPY_SUP_L2',
  'ROLE_THERAPY_SUP_L3',
  'ROLE_CLINIC_SUCCESS_MANAGER',
  'ROLE_CLINIC_SUCCESS_ADMIN',
  'ROLE_ADMIN',
]
const DOCSIDE_ROLES = ['ROLE_DOCSIDE_ADMIN']

function r(relativePath, fullPath, rest) {
  return { path: relativePath, fullPath: fullPath ?? (relativePath ? `${P}/${relativePath}` : P), ...rest }
}

export const supportRoutes = [
  r('', P, { exact: true, name: 'Support Home', Element: SupportHomePage }),
  r('practitioner-management/onboarding', null, {
    exact: true,
    name: 'Practitioner Onboarding',
    Element: PractitionerOnboardingPage,
    allowedRoles: PRACTITIONER_ONBOARDING_ROLES,
  }),
  r('practitioner-management/onboarding/auditor', null, {
    exact: true,
    name: 'Practitioner Auditor Access',
    Element: PractitionerOnboardingPage,
    allowedRoles: PRACTITIONER_ONBOARDING_ROLES,
  }),
  r('practitioner-management/onboarding/payment', null, {
    exact: true,
    name: 'Practitioner Payment Configuration',
    Element: PractitionerPaymentConfigPage,
    allowedRoles: PRACTITIONER_ONBOARDING_ROLES,
  }),
  r('practitioner-management/therapists', null, { exact: true, name: 'Therapists Listing', Element: TherapistsListing }),
  r('practitioner-management/applicants', null, {
    exact: true,
    name: 'Therapist Applicants',
    Element: TherapistApplicantsPage,
  }),
  r('practitioner-management/screeners', null, {
    exact: true,
    name: 'Screeners Listing',
    Element: ScreenersPage,
  }),
  r('practitioner-management/invitations', null, {
    exact: true,
    name: 'Practitioner Invitations',
    Element: PractitionerInvitationsPage,
    allowedRoles: PRACTITIONER_ONBOARDING_ROLES,
  }),
  // Phase 14i — kyc-approved-but-disabled practitioners landing.
  r('practitioner-management/disabled-practitioners', null, {
    exact: true,
    name: 'Disabled Practitioners',
    Element: DisabledPractitionersPage,
    allowedRoles: PRACTITIONER_ONBOARDING_ROLES,
  }),
  // Platform-wide practitioner enable/disable access log.
  r('practitioner-management/access-logs', null, {
    exact: true,
    name: 'Practitioner Access Logs',
    Element: PractitionerAccessLogsPage,
  }),
  // Screener-context detail + in-context action sub-routes. The sub-route Elements are the
  // same generic practitioner views the therapist routes use — screeners just resolve under
  // their own /screeners/:mentorSlug base path so the context stays correct.
  r('practitioner-management/screeners/:mentorSlug/roster', null, { exact: true, name: 'Screener Roster View', Element: PractitionerRosterView }),
  r('practitioner-management/screeners/:mentorSlug/calendar', null, { exact: true, name: 'Single Screener Roster', Element: SingleTherapistRosterPage }),
  r('practitioner-management/screeners/:mentorSlug/engagement-models', null, { exact: true, name: 'Screener Engagement Models', Element: TherapistEngagementModels }),
  r('practitioner-management/screeners/:mentorSlug/events', null, { exact: true, name: 'Single Screener Events', Element: SingleTherapistEventsPage }),
  r('practitioner-management/screeners/:mentorSlug/appointments', null, { exact: true, name: 'Screener Appointments', Element: SupportTherapistAppointmentsPage }),
  r('practitioner-management/screeners/:mentorSlug/patients', null, { exact: true, name: 'Screener Patients', Element: React.lazy(() => import('../views/practitioners/patients/SupportPractitionerPatientsPage')) }),
  r('practitioner-management/screeners/:mentorSlug/emr-settings', null, { exact: true, name: 'Screener EMR Settings', Element: SupportDoctorSettings }),
  r('practitioner-management/screeners/:mentorSlug/payout-receipts', null, { exact: true, name: 'Screener Payout Receipts', Element: TherapistPayoutReceipts }),
  r('practitioner-management/screeners/:mentorSlug', null, { exact: true, name: 'Screener Clinics Listing', Element: ScreenerClinics }),
  r('clinics-hospitals/clinic-roster', null, { exact: true, name: 'Clinic Roster', Element: ClinicRosterPage }),
  r('roster-management/overall-roster', null, { exact: true, name: 'Overall Roster', Element: OverallRosterPage }),
  r('therapist-roster', null, { exact: true, name: 'Roster', Element: TherapistRosterPage }),
  r('practitioner-management/therapists/:mentorSlug/roster', null, { exact: true, name: 'Practitioner Roster View', Element: PractitionerRosterView }),
  r('practitioner-management/therapists/:mentorSlug/calendar', null, { exact: true, name: 'Single Therapist Roster', Element: SingleTherapistRosterPage }),
  r('practitioner-management/therapists/:mentorSlug/payouts', null, {
    exact: true,
    name: 'Therapist Payout Workspace',
    Element: TherapistPayoutHubPage,
    allowedRoles: THERAPIST_PAYOUT_REPORT_ROLES,
  }),
  r('practitioner-management/therapists/:mentorSlug/communications', null, {
    exact: true,
    name: 'Therapist Communications',
    Element: TherapistCommunicationsPage,
  }),
  r('practitioner-management/therapists/:mentorSlug/payout-reports', null, {
    exact: true,
    name: 'Therapist Payout Reports',
    Element: TherapistPayoutReports,
    allowedRoles: THERAPIST_PAYOUT_REPORT_ROLES,
  }),
  r('practitioner-management/therapists/:mentorSlug/engagement-models', null, { exact: true, name: 'Therapist Engagement Models', Element: TherapistEngagementModels }),
  r('practitioner-management/therapists/:mentorSlug/events', null, { exact: true, name: 'Single Therapist Events', Element: SingleTherapistEventsPage }),
  r('practitioner-management/therapists/:mentorSlug/appointments', null, { exact: true, name: 'Therapist Appointments', Element: SupportTherapistAppointmentsPage }),
  r('practitioner-management/therapists/:mentorSlug/bundle-packs', null, { exact: true, name: 'Therapist Bundle Packs', Element: SupportPractitionerBundlePacksPage }),
  r('practitioner-management/therapists/:mentorSlug/patients', null, { exact: true, name: 'Therapist Patients', Element: React.lazy(() => import('../views/practitioners/patients/SupportPractitionerPatientsPage')) }),
  r('practitioner-management/doctors/:mentorSlug/appointments', null, { exact: true, name: 'Doctor Appointments', Element: SupportTherapistAppointmentsPage, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/patients', null, { exact: true, name: 'Doctor Patients', Element: React.lazy(() => import('../views/practitioners/patients/SupportPractitionerPatientsPage')), allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/events', null, { exact: true, name: 'Single Doctor Events', Element: DoctorsEventTypes, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/payout-receipts', null, { exact: true, name: 'Single Doctor Payout Receipts', Element: DoctorsPayoutReceipts, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/payout-receipts', null, { exact: true, name: 'Payout Receipts', Element: PayoutReceipts }),
  r('clinics-hospitals/clinic-brand-invoices', null, {
    exact: true,
    name: 'Clinic Brand Invoices',
    Element: ClinicBrandInvoices,
    allowedRoles: ['ROLE_CLINIC_SUCCESS_MANAGER', 'ROLE_CLINIC_SUCCESS_ADMIN'],
  }),
  r('practitioner-management/doctors/:mentorSlug/emr-settings', null, { exact: true, name: 'Doctor EMR Settings', Element: SupportDoctorSettings, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/therapists/:mentorSlug/emr-settings', null, { exact: true, name: 'Therapist EMR Settings', Element: SupportDoctorSettings }),
  r('patient-management/therapy-scheduled-patients', null, { exact: true, name: 'Therapy Scheduled Patients', Element: ScheduledPatients, eventType: EVENT_TYPE_THERAPY_SESSION }),
  r('patient-management/consultation-scheduled-patients', null, { exact: true, name: 'Therapy Scheduled Patients', Element: ScheduledPatients, eventType: EVENT_TYPE_CONSULTATION }),
  r('practitioner-management/therapists/:mentorSlug', null, { exact: true, name: 'Therapist Clinics Listing', Element: TherapistsClinics }),
  r('practitioner-management/therapists/:mentorSlug/payout-receipts', null, { exact: true, name: 'Therapist Payout Receipts', Element: TherapistPayoutReceipts }),
  r('practitioner-management/therapists/:mentorSlug/:clinicLocationSlug', null, { exact: true, name: 'Therapist Day View Cal', Element: TherapistDayViewCal }),
  r('practitioner-management/therapists/:mentorSlug/:clinicLocationSlug/:bookedEventSlug', null, { exact: true, name: 'Therapist Reports Viewer', Element: SupportReportsViewer }),
  r('practitioner-management/doctors', null, { exact: true, name: 'Doctors Listing', Element: DoctorsListing, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/roster', null, { exact: true, name: 'Doctor Roster View', Element: PractitionerRosterView, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug', null, { exact: true, name: 'Doctors Clinics Listing', Element: DoctorsClinics, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/:clinicLocationSlug', null, { exact: true, name: 'Doctors Day View Cal', Element: DoctorDayViewCal, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/prescription-templates', null, { exact: true, name: 'Support Prescription Templates', Element: SupportPrescriptionTemplates, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('practitioner-management/doctors/:mentorSlug/prescription-templates/:clinicId', null, { exact: true, name: 'Support Prescription Template View', Element: SupportPrescriptionTemplateViewPage, allowedRoles: DOCTOR_SUPPORT_ROLES }),
  r('clinics-hospitals/clinics', null, { exact: true, name: 'Clinics Listing', Element: ClinicsListing }),
  r('clinics-hospitals/clinics/:clinicLocationSlug/appointments', null, { exact: true, name: 'Clinic Location Appointments', Element: React.lazy(() => import('../views/clinics/appointments/SupportClinicAppointmentsPage')) }),
  r('clinics-hospitals/clinics/:clinicBrandSlug/patients', null, { exact: true, name: 'Clinic Brand Patients', Element: SupportClinicPatientsPage }),
  r('clinics-hospitals/clinics/:clinicSlug/services', null, { exact: true, name: 'Clinic Services', Element: SupportClinicServices }),
  r('clinics-hospitals/clinics/:clinicSlug/bundle-packs', null, { exact: true, name: 'Clinic Location Bundle Packs', Element: SupportClinicLocationBundlePacksPage }),
  r('clinics-hospitals/clinics/:brandSlug/brand-bundle-packs', null, { exact: true, name: 'Clinic Brand Bundle Packs', Element: SupportClinicBrandBundlePacksPage }),
  r('clinics-hospitals/clinic-services/direct-entry', null, { exact: true, name: 'Clinic Services Direct Entry', Element: SupportClinicServicesDirectEntry }),
  r('clinics-hospitals/clinics/:brandSlug/payout-invoices', null, {
    exact: true,
    name: 'Clinic Payout Invoices',
    Element: ClinicBrandPayoutInvoices,
    allowedRoles: ['ROLE_CLINIC_SUCCESS_MANAGER', 'ROLE_CLINIC_SUCCESS_ADMIN'],
  }),
  r('clinics-hospitals/clinics/:clinicSlug/unassigned-bookings', null, { exact: true, name: 'Clinic Unassigned Bookings', Element: SupportClinicBookings }),
  r('clinics-hospitals/clinics/:brandSlug/payout-reports', null, { exact: true, name: 'Clinic Payout Reports', Element: SingularClinicBrandPayoutReports }),
  r('clinics-hospitals/clinics/:brandSlug/engagement-models', null, { exact: true, name: 'Clinic Engagement Models', Element: SingularClinicBrandEngagementModels }),
  r('clinics-hospitals/clinics/:clinicLocationSlug/:mentorSlug', null, { exact: true, name: 'Clinic Practitioner Calendar', Element: TherapistDayViewCal }),
  r('clinics-hospitals', null, { exact: true, name: 'Clinics & Hospitals', Element: ClinicsNavPage }),
  r('patient-management', null, { exact: true, name: 'Patients', Element: PatientsNavPage }),
  r('practitioner-management', null, { exact: true, name: 'Practitioners & Service Providers', Element: PractitionerManagementNavPage, allowedRoles: PRACTITIONER_NAV_ROLES }),
  r('patient-management/therapy-actions', null, { exact: true, name: 'Patients', Element: PatientsTherapyActions }),
  r('patient-management/care-orchestration', null, {
    exact: true,
    name: 'Care Orchestration',
    Element: CareOrchestrationCasesPage,
    allowedRoles: CARE_ORCHESTRATION_ROLES,
  }),
  r('patient-management/list', null, { exact: true, name: 'Patient List', Element: PatientList }),
  r('patient-management/prospects', null, {
    exact: true,
    name: 'Patient Prospects',
    Element: PatientProspectsListPage,
    allowedRoles: ['ROLE_THERAPY_SUP_L2', 'ROLE_THERAPY_SUP_L3', 'ROLE_PATIENT_SUPPORT_L3', 'ROLE_CLINIC_SUCCESS_MANAGER', 'ROLE_CLINIC_SUCCESS_ADMIN'],
  }),
  r('booked-events', null, {
    exact: true,
    name: 'Bookings & Appointments',
    Element: BookedEvents,
    allowedRoles: [...SUPPORT_BOOKINGS_VIEW_ROLES, ...SUPPORT_VC_AUDIT_VIEW_ROLES],
  }),
  r('case-dispatch', null, {
    exact: true,
    name: 'Case Dispatch',
    Element: CaseDispatchConsolePage,
    allowedRoles: SUPPORT_CASE_DISPATCH_ROLES,
  }),
  r('payment-links', null, {
    exact: true,
    name: 'Payment Links',
    Element: SupportPaymentLinksPage,
    allowedRoles: SUPPORT_PAYMENT_LINKS_VIEW_ROLES,
  }),
  r('important-actions/custom-communications', null, {
    exact: true,
    name: 'Custom Communications',
    Element: CustomCommunicationsPage,
    allowedRoles: SUPPORT_CUSTOM_COMMUNICATIONS_VIEW_ROLES,
  }),
  r('important-actions/custom-communications/call', null, {
    exact: true,
    name: 'Call',
    Element: CustomCommunicationsPage,
    allowedRoles: SUPPORT_CUSTOM_COMMUNICATIONS_VIEW_ROLES,
  }),
  r('important-actions/custom-communications/whatsapp', null, {
    exact: true,
    name: 'Whatsapp',
    Element: CustomCommunicationsPage,
    allowedRoles: SUPPORT_CUSTOM_COMMUNICATIONS_VIEW_ROLES,
  }),
  r('management-reporting', null, {
    exact: true,
    name: 'Management Reporting',
    Element: ManagementReportingBookingsRedirect,
    allowedRoles: ['ROLE_MANAGEMENT'],
  }),
  r('management-reporting/bookings', null, {
    exact: true,
    name: 'Bookings',
    Element: BookingReportingPage,
    allowedRoles: ['ROLE_MANAGEMENT'],
  }),
  r('booking-reporting', null, {
    exact: true,
    name: 'Management Reporting',
    Element: ManagementReportingBookingsRedirect,
    allowedRoles: ['ROLE_MANAGEMENT'],
  }),
  r('booked-events/:bookedEventId/live', null, {
    exact: true,
    name: 'Live Session Viewer',
    Element: VcAuditSessionLiveViewer,
    allowedRoles: ['ROLE_VC_AUDIT_ADMIN', 'ROLE_VC_AUDIT_MANAGER'],
  }),
  r('booked-events/:bookedEventId/recordings', null, {
    exact: true,
    name: 'Recording Playback',
    Element: VcAuditRecordingPlayer,
    allowedRoles: ['ROLE_VC_AUDIT_ADMIN', 'ROLE_VC_AUDIT_MANAGER'],
  }),
  r('booked-events/:bookedEventId/recordings/:recordingId', null, {
    exact: true,
    name: 'Recording Playback',
    Element: VcAuditRecordingPlayer,
    allowedRoles: ['ROLE_VC_AUDIT_ADMIN', 'ROLE_VC_AUDIT_MANAGER'],
  }),
  r('prescription-interventions', null, { exact: true, name: 'Prescription Interventions', Element: PrescriptionInterventionsListing, allowedRoles: INTERVENTION_ROLES }),
  r('practitioner-management/auditors/audit', null, { exact: true, name: 'Audit Management', Element: AuditPage }),
  r('practitioner-management/auditors/audit-requests', null, { exact: true, name: 'Audit Requests', Element: AuditRequestsListing }),
  r('doc-side', null, { exact: true, name: 'DocSide Products', Element: DocSideProducts, allowedRoles: DOCSIDE_ROLES }),
  r('doc-side/:productPackageId/contexts', null, { exact: true, name: 'DocSide Contexts', Element: DocSideContexts, allowedRoles: DOCSIDE_ROLES }),
  r('doc-side/:productPackageId/contexts/:contextId/roles', null, { exact: true, name: 'DocSide Roles', Element: DocSideRoles, allowedRoles: DOCSIDE_ROLES }),
  r('doc-side/:productPackageId/contexts/:contextId/roles/:roleId/content', null, { exact: true, name: 'DocSide Content Management', Element: DocSideContent, allowedRoles: DOCSIDE_ROLES }),
  r('practitioner-management/service-approvals', null, { exact: true, name: 'Service Approvals', Element: ServiceApprovals, allowedRoles: SERVICE_APPROVAL_ROLES }),
]
